import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import MedicationOutlinedIcon from '@mui/icons-material/MedicationOutlined'
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined'
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import { Box, Button, Tooltip } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import BillPatientDialog from './BillPatientDialog'
import SendAttachmentDialog from './SendAttachmentDialog'
import SendGoogleReviewDialog from './SendGoogleReviewDialog'
import SendPaymentLinkDialog from './SendPaymentLinkDialog'
import SendPrescriptionDialog from './SendPrescriptionDialog'
import { loadQuickActionCapabilities } from './patientQuickActionsService'

// The full action set, in the order product asked for. `ready` marks which
// actions are wired end-to-end in this phase; the rest render as gated
// "Coming soon" buttons so the entry point is complete and reviewable while
// each send-action is implemented one-by-one in a later phase.
//   'book'       -> enabled when the surface provides an onBookSlot handler
//   'attachment' -> enabled when the contact context can be keyed
//   false        -> gated (Coming soon)
const ACTION_DEFS = [
  { id: 'send-prescription', label: 'Send Prescription', Icon: MedicationOutlinedIcon, ready: 'prescription' },
  { id: 'book-slot', label: 'Book Slot', Icon: EventAvailableOutlinedIcon, ready: 'book' },
  { id: 'send-payment-link', label: 'Send Payment Link', Icon: PaymentOutlinedIcon, ready: 'payment' },
  { id: 'bill-patient', label: 'Bill Patient', Icon: ReceiptLongOutlinedIcon, ready: 'receipt' },
  { id: 'send-review', label: 'Send Google Review Link', Icon: RateReviewOutlinedIcon, ready: 'review' },
  { id: 'send-attachment', label: 'Send Attachment', Icon: AttachFileRoundedIcon, ready: 'attachment' },
]

function hasContactKeying(context) {
  return !!(context?.bookedEventId || context?.patientId)
}

/**
 * Shared "More actions" button group surfaced on the mentor session page, the
 * mentor patient drawer, and the support patient drawer.
 *
 * @param {object}   contactContext  { source, bookedEventId?, patientId?, clinicBrandId?, clinicId?, clinicBrandName?, patientName?, patientMobile?, patientEmail? }
 * @param {function} onBookSlot      opens the booking popup for this patient (surface-specific); Book Slot is gated when omitted
 * @param {string[]} availableActions optional subset/reordering of action ids
 * @param {'inline'|'stack'} layout  row (default) or full-width column
 * @param {boolean}  dense           smaller buttons
 * @param {object}   sx              container style override
 */
export default function PatientQuickActions({
  contactContext,
  onBookSlot,
  availableActions,
  layout = 'inline',
  dense = false,
  sx = {},
}) {
  const [attachmentOpen, setAttachmentOpen] = useState(false)
  const [prescriptionOpen, setPrescriptionOpen] = useState(false)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [billOpen, setBillOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [caps, setCaps] = useState(null)

  const isSupport = contactContext?.source === 'support-patient'

  // Pre-load deliverability so we can disable actions that would silently fail (no channel on file,
  // clinic comms policy, unconfigured Google review, no patient account) and show why. Mentor sources
  // only — the support capabilities response has a different shape and support scopes its own actions.
  useEffect(() => {
    let active = true
    setCaps(null)
    if (isSupport || !hasContactKeying(contactContext)) {
      return undefined
    }
    loadQuickActionCapabilities(contactContext).then((result) => {
      if (active) {
        setCaps(result)
      }
    })
    return () => {
      active = false
    }
  }, [contactContext, isSupport])

  const actions = useMemo(() => {
    if (!Array.isArray(availableActions) || availableActions.length === 0) {
      return ACTION_DEFS
    }
    return availableActions
      .map((id) => ACTION_DEFS.find((action) => action.id === id))
      .filter(Boolean)
  }, [availableActions])

  const canBook = typeof onBookSlot === 'function'
  const canKey = hasContactKeying(contactContext)
  const isStack = layout === 'stack'

  const resolveState = (action) => {
    if (!action.ready) {
      return { enabled: false, hint: 'Coming soon' }
    }
    // Book Slot is a local popup (no send) — available whenever the surface wires a handler.
    if (action.ready === 'book') {
      return { enabled: canBook, hint: canBook ? '' : 'Booking is not available here' }
    }
    if (!canKey) {
      return { enabled: false, hint: 'Patient contact is not available' }
    }
    // Clinical/billing sends stay practitioner-scoped; gated for the support source.
    const practitionerOnly =
      action.ready === 'prescription' || action.ready === 'payment' || action.ready === 'receipt'
    if (practitionerOnly && isSupport) {
      return { enabled: false, hint: 'A practitioner action' }
    }
    // Deliverability pre-check (mentor sources). Until capabilities load we stay optimistic (enabled).
    if (caps?.ok) {
      // Attachments go out over WhatsApp only.
      if (action.ready === 'attachment' && !caps.whatsappEnabled) {
        return { enabled: false, hint: caps.whatsappReason || 'WhatsApp is not available for this patient.' }
      }
      // Everything else needs at least one deliverable channel (WhatsApp or email).
      if (action.ready !== 'attachment' && !caps.anyChannelEnabled) {
        return {
          enabled: false,
          hint: caps.whatsappReason || caps.emailReason || 'No WhatsApp or email on file for this patient.',
        }
      }
      // Payment link is a per-user Razorpay link — needs the patient to have an account.
      if (action.ready === 'payment' && !caps.patientHasAccount) {
        return { enabled: false, hint: 'This patient has no account to receive a payment link.' }
      }
      // Google review needs the clinic to have configured a review destination.
      if (action.ready === 'review' && !caps.googleReviewConfigured) {
        return { enabled: false, hint: "This clinic hasn't set up a Google review link yet." }
      }
    }
    return { enabled: true, hint: '' }
  }

  const handleClick = (action) => {
    if (action.id === 'book-slot') {
      onBookSlot?.()
    } else if (action.id === 'send-attachment') {
      setAttachmentOpen(true)
    } else if (action.id === 'send-prescription') {
      setPrescriptionOpen(true)
    } else if (action.id === 'send-payment-link') {
      setPaymentOpen(true)
    } else if (action.id === 'bill-patient') {
      setBillOpen(true)
    } else if (action.id === 'send-review') {
      setReviewOpen(true)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isStack ? 'column' : 'row',
        flexWrap: isStack ? 'nowrap' : 'wrap',
        gap: 1,
        ...sx,
      }}
    >
      {actions.map((action) => {
        const { enabled, hint } = resolveState(action)
        const button = (
          <Button
            variant="outlined"
            size={dense ? 'small' : 'medium'}
            fullWidth={isStack}
            disabled={!enabled}
            onClick={() => handleClick(action)}
            startIcon={<action.Icon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              justifyContent: isStack ? 'flex-start' : 'center',
              borderColor: 'rgba(67, 56, 202, 0.28)',
              color: 'rgba(67, 56, 202, 0.95)',
              '&:hover': { borderColor: 'rgba(67, 56, 202, 0.6)', backgroundColor: 'rgba(67, 56, 202, 0.06)' },
            }}
          >
            {action.label}
          </Button>
        )
        if (enabled) {
          return <Box key={action.id} sx={{ display: isStack ? 'block' : 'inline-flex' }}>{button}</Box>
        }
        return (
          <Tooltip key={action.id} title={hint} placement="top">
            <Box sx={{ display: isStack ? 'block' : 'inline-flex' }}>{button}</Box>
          </Tooltip>
        )
      })}

      <SendAttachmentDialog
        open={attachmentOpen}
        onClose={() => setAttachmentOpen(false)}
        contactContext={contactContext}
        patientName={contactContext?.patientName}
      />
      <SendPrescriptionDialog
        open={prescriptionOpen}
        onClose={() => setPrescriptionOpen(false)}
        contactContext={contactContext}
        patientName={contactContext?.patientName}
      />
      <SendPaymentLinkDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        contactContext={contactContext}
        patientName={contactContext?.patientName}
      />
      <BillPatientDialog
        open={billOpen}
        onClose={() => setBillOpen(false)}
        contactContext={contactContext}
        patientName={contactContext?.patientName}
      />
      <SendGoogleReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        contactContext={contactContext}
        patientName={contactContext?.patientName}
      />
    </Box>
  )
}
